import type React from 'react';

import { GroupWorkRounded } from '@mui/icons-material';
import { Chip } from '@mui/material';
import {
  FiqlInput,
  FiqlLabelsInput,
  FiqlReference,
  OrdReferenceField,
  OrdDatagrid,
  OrdCreateDialog,
  OrdEditDialog,
  OrdShowDialog,
  OrdList,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { HuntingGroupFields } from '@ragu-components/ord-types';
import { FunctionField, TextField } from 'react-admin';

import { HuntingGroupsForm } from './HuntingGroupsForm';

import type { ResourceType } from '../../personal';
import type { OrdDatagridProps, OrdListProps } from '@ragu-components/ord-components';
import type { HuntingGroup } from '@ragu-components/ord-types';

interface HuntingGroupListProps extends Partial<Omit<OrdListProps, 'empty'>>, ResourceType {
  embeddedChildren?: React.ReactElement | React.ReactElement[];
  empty?: OrdDatagridProps['empty'];
}

/**
 * Generates a list of hunting groups with various actions and layouts.
 *
 * @param {HuntingGroupListProps} embeddedChildren - the embedded children to render
 * @param {boolean} empty - flag indicating if the list is empty
 * @param {HuntingGroupListProps} props - additional props for the hunting group list
 * @return {React.ReactElement} the rendered hunting group list component
 */
export const HuntingGroupList = ({
  embeddedChildren = undefined,
  empty = undefined,
  layout = 'full',
  ...props
}: HuntingGroupListProps): React.ReactElement => (
  <>
    <OrdList
      icon={<GroupWorkRounded />}
      layout={layout}
      quickFilters={
        layout === 'full'
          ? [
              <FiqlReference
                key='owner'
                label='resources.hunting_groups.fields.owner'
                resource={Resources.users}
                source={HuntingGroupFields['owner.id']}
              />,
              <FiqlInput key='name' type='string' resettable source={HuntingGroupFields.name} />,
              <FiqlInput key='description' type='string' resettable source={HuntingGroupFields.description} />,
              <FiqlLabelsInput key='labels' source={HuntingGroupFields['labels.id']} />,
            ]
          : []
      }
      sort={{ field: HuntingGroupFields.name, order: 'ASC' }}
      {...props}
    >
      <OrdDatagrid layout={layout} empty={empty}>
        <TextField source={HuntingGroupFields.name} />
        <TextField source={HuntingGroupFields.description} />
        <FunctionField
          sortable={false}
          source={HuntingGroupFields.user_numbers}
          render={(record: HuntingGroup) =>
            record.user_numbers
              ? record.user_numbers.map((userNumber) => <Chip key={userNumber.id} label={userNumber.number} />)
              : ''
          }
        />
        {layout === 'full' && <OrdReferenceField source={HuntingGroupFields.owner} reference={Resources.users} />}
        {embeddedChildren}
      </OrdDatagrid>
    </OrdList>
    {layout === 'full' && (
      <OrdCreateDialog>
        <HuntingGroupsForm />
      </OrdCreateDialog>
    )}
    {layout === 'full' && (
      <OrdEditDialog>
        <HuntingGroupsForm />
      </OrdEditDialog>
    )}
    {layout === 'full' && (
      <OrdShowDialog>
        <HuntingGroupsForm />
      </OrdShowDialog>
    )}
  </>
);

export default HuntingGroupList;
